import Sparkline from './Sparkline';
import type { ConditionalFormatRule } from '../../../../types/bi';
import styles from './KpiCard.module.css';

interface KpiCardProps {
  value: number | string;
  label: string;
  sparklineData?: number[];
  delta?: number | null;
  deltaPct?: number | null;
  format?: 'number' | 'currency' | 'percentage';
  conditionalRules?: ConditionalFormatRule[];
  prefix?: string;
  postfix?: string;
  unit?: string;
  thousandsSeparator?: ',' | '.' | ' ' | 'none';
  decimalSeparator?: '.' | ',';
  decimalPlaces?: number;
}

function formatValue(
  v: number | string,
  format: 'number' | 'currency' | 'percentage',
  prefix?: string,
  postfix?: string,
  unit?: string,
  thousandsSeparator: ',' | '.' | ' ' | 'none' = ',',
  decimalSeparator: '.' | ',' = '.',
  decimalPlaces?: number,
): string {
  if (typeof v === 'string') return v;

  const defaults = {
    currency: 2,
    percentage: 1,
    number: 0,
  } as const;

  const clampedPlaces =
    typeof decimalPlaces === 'number' && Number.isFinite(decimalPlaces)
      ? Math.max(0, Math.min(6, Math.trunc(decimalPlaces)))
      : defaults[format];

  let formatted = v.toLocaleString('en-GB', {
    minimumFractionDigits: clampedPlaces,
    maximumFractionDigits: clampedPlaces,
    useGrouping: thousandsSeparator !== 'none',
  });

  if (thousandsSeparator === '.') formatted = formatted.replace(/,/g, '.');
  if (thousandsSeparator === ' ') formatted = formatted.replace(/,/g, ' ');
  if (thousandsSeparator === 'none') formatted = formatted.replace(/,/g, '');
  if (decimalSeparator === ',') formatted = formatted.replace(/\./g, ',');

  const resolvedPrefix = prefix ?? (format === 'currency' ? '£' : '');
  const resolvedPostfix = postfix ?? (format === 'percentage' ? '%' : '');
  const resolvedUnit = unit ? ` ${unit}` : '';
  return `${resolvedPrefix}${formatted}${resolvedPostfix}${resolvedUnit}`;
}

function evaluateColour(value: number, rules: ConditionalFormatRule[]): string | undefined {
  for (const rule of rules) {
    switch (rule.rule) {
      case 'gt':
        if (value > rule.threshold) return rule.colour;
        break;
      case 'lt':
        if (value < rule.threshold) return rule.colour;
        break;
      case 'gte':
        if (value >= rule.threshold) return rule.colour;
        break;
      case 'lte':
        if (value <= rule.threshold) return rule.colour;
        break;
      case 'eq':
        if (value === rule.threshold) return rule.colour;
        break;
      case 'between':
        if (
          rule.threshold_upper != null &&
          value >= rule.threshold &&
          value <= rule.threshold_upper
        )
          return rule.colour;
        break;
    }
  }
  return undefined;
}

export default function KpiCard({
  value,
  label,
  sparklineData,
  delta,
  deltaPct,
  format = 'number',
  conditionalRules,
  prefix,
  postfix,
  unit,
  thousandsSeparator,
  decimalSeparator,
  decimalPlaces,
}: KpiCardProps) {
  const numericValue = typeof value === 'number' ? value : parseFloat(String(value));
  const condColour =
    conditionalRules && !isNaN(numericValue)
      ? evaluateColour(numericValue, conditionalRules)
      : undefined;

  const isPositive = delta != null && delta > 0;
  const isNegative = delta != null && delta < 0;

  return (
    <div className={styles.card}>
      <div className={styles.label}>{label}</div>
      <div className={styles.value} style={condColour ? { color: condColour } : undefined}>
        {formatValue(
          value,
          format,
          prefix,
          postfix,
          unit,
          thousandsSeparator,
          decimalSeparator,
          decimalPlaces,
        )}
      </div>
      {delta != null && (
        <div
          className={`${styles.delta} ${isPositive ? styles.positive : ''} ${isNegative ? styles.negative : ''}`}
        >
          <span className={styles.arrow}>{isPositive ? '▲' : isNegative ? '▼' : '—'}</span>{' '}
          {Math.abs(delta).toLocaleString('en-GB')}
          {deltaPct != null && ` (${deltaPct > 0 ? '+' : ''}${deltaPct.toFixed(1)}%)`}
        </div>
      )}
      {sparklineData && sparklineData.length > 1 && (
        <div className={styles.sparkline}>
          <Sparkline data={sparklineData} colour={condColour} />
        </div>
      )}
    </div>
  );
}
